import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Category } from '@/types/category';
import { useLanguage } from '@/context/LanguageContext';

interface Props {
  title: string;
  subtitle: string;
  categories: Category[];
}

const categorySlug = (name: string) => name.toLowerCase().replace(/\s+/g, '-');

const CategoryShowcaseSection: React.FC<Props> = ({ title, subtitle, categories }) => {
  const { tl } = useLanguage();
  const displayCategories = categories.slice(0, 4);

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="container-custom">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left: stays visible while right column scrolls */}
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <h2 className="max-w-[14ch] text-3xl font-semibold leading-[1.15] tracking-tight text-black md:text-4xl lg:text-[2.75rem]">
              {title}
            </h2>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-neutral-500 md:text-[15px]">
              {subtitle}
            </p>
            <Link
              to="/products"
              className="mt-8 inline-flex items-center justify-center bg-black px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-opacity hover:opacity-80"
            >
              {tl('Voir tout', 'View All', 'Vedi tutto')}
            </Link>
          </div>

          {/* Right: staggered category rows */}
          <div className="flex flex-col gap-14 lg:col-span-8 lg:gap-16">
            {displayCategories.map((cat, index) => {
              const imageFirst = index % 2 === 0;
              const description =
                cat.description?.trim() ||
                tl(
                  `Découvrez notre sélection ${cat.name.toLowerCase()}.`,
                  `Explore our curated ${cat.name.toLowerCase()} selection.`,
                  `Scoprite la nostra selezione di ${cat.name.toLowerCase()}.`
                );

              const imageBlock = (
                <Link
                  to={`/category/${categorySlug(cat.name)}`}
                  className="block aspect-square w-full overflow-hidden bg-neutral-100"
                >
                  <img
                    src={cat.image || '/placeholder.svg'}
                    alt={cat.name}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]"
                    loading="lazy"
                  />
                </Link>
              );

              const textBlock = (
                <div className="flex h-full flex-col justify-center">
                  <h3 className="text-xl font-semibold tracking-tight text-black md:text-2xl">
                    {cat.name}
                  </h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-neutral-500">
                    {description}
                  </p>
                  <Link
                    to={`/category/${categorySlug(cat.name)}`}
                    className="mt-8 inline-flex w-full max-w-xs items-center justify-between border-b border-neutral-300 pb-3 text-sm font-medium text-black transition-colors hover:border-black"
                  >
                    <span>
                      {tl('Parcourir la catégorie', 'Browse Category', 'Sfoglia la categoria')}
                    </span>
                    <ArrowUpRight className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                  </Link>
                </div>
              );

              return (
                <div
                  key={cat.id}
                  className="grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-10"
                >
                  {imageFirst ? (
                    <>
                      {imageBlock}
                      {textBlock}
                    </>
                  ) : (
                    <>
                      {textBlock}
                      {imageBlock}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoryShowcaseSection;
